import { z } from 'zod';
import { creditLedgerEntrySchema } from '../schemas/credit';
import { jobKindSchema, jobStatusSchema, storyboardJobPhaseSchema } from '../schemas/job';
import type { UploadProcessingStatus } from '../schemas/upload';

const uploadStatusValues: [UploadProcessingStatus, ...UploadProcessingStatus[]] = [
  'pending',
  'ready',
  'blocked',
];

export const realtimeEventVersion = 1 as const;

const baseEventSchema = z.object({
  id: z.string().uuid(),
  timestamp: z.string().datetime(),
  userId: z.string(),
  version: z.literal(realtimeEventVersion),
  correlationId: z.string().optional(),
  channel: z.string().optional(),
});

const jobErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  rootCause: z.string().optional(),
});

const jobOutputSnapshotSchema = z.object({
  assetUrl: z.string().url().optional(),
  thumbnailUrl: z.string().url().optional(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const jobUpdatedEventSchema = baseEventSchema.extend({
  type: z.literal('job.updated'),
  data: z.object({
    jobId: z.string(),
    orgId: z.string().optional(),
    clientId: z.string().optional(),
    projectId: z.string().optional(),
    status: jobStatusSchema,
    previousStatus: jobStatusSchema.optional(),
    kind: jobKindSchema,
    model: z.string(),
    updatedAt: z.string().datetime(),
    completedAt: z.string().datetime().optional(),
    cancelRequestedAt: z.string().datetime().optional(),
    cost: z.number().optional(),
    output: jobOutputSnapshotSchema.optional(),
    error: jobErrorSchema.optional(),
  }),
});

export const uploadUpdatedEventSchema = baseEventSchema.extend({
  type: z.literal('upload.updated'),
  data: z.object({
    uploadId: z.string(),
    status: z.enum(uploadStatusValues),
    storagePath: z.string(),
    storageBucket: z.string(),
    assetUrl: z.string().url().nullable(),
    reason: z.string().nullable().optional(),
    scannedAt: z.string().datetime().nullable().optional(),
    signature: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
});

export const creditsChangedEventSchema = baseEventSchema.extend({
  type: z.literal('credits.changed'),
  data: z.object({
    balance: z.number().int(),
    delta: z.number().int(),
    ledgerEntry: creditLedgerEntrySchema.optional(),
  }),
});

export const billingAlertCodeSchema = z.enum([
  'invoice_paid',
  'invoice_payment_failed',
  'payment_method_required',
  'subscription_canceled',
  'subscription_past_due',
]);

export const billingAlertEventSchema = baseEventSchema.extend({
  type: z.literal('billing.alert'),
  data: z.object({
    code: billingAlertCodeSchema,
    message: z.string(),
    context: z.record(z.string(), z.unknown()).optional(),
  }),
});

export const storyboardUpdatedEventSchema = baseEventSchema.extend({
  type: z.literal('storyboard.updated'),
  data: z.object({
    storyboardId: z.string(),
    phase: storyboardJobPhaseSchema,
    status: z.enum(['started', 'progress', 'complete', 'failed']),
    progress: z.number().min(0).max(100).optional(),
    itemIndex: z.number().int().optional(),
    itemCount: z.number().int().optional(),
    queueStatus: z.string().optional(),
    queuePosition: z.number().int().nonnegative().optional(),
    error: z
      .object({
        code: z.string(),
        message: z.string(),
        rootCause: z.string().optional(),
      })
      .optional(),
  }),
});

export const realtimeEventSchema = z.discriminatedUnion('type', [
  jobUpdatedEventSchema,
  uploadUpdatedEventSchema,
  creditsChangedEventSchema,
  billingAlertEventSchema,
  storyboardUpdatedEventSchema,
]);

export type JobUpdatedEvent = z.infer<typeof jobUpdatedEventSchema>;
export type UploadUpdatedEvent = z.infer<typeof uploadUpdatedEventSchema>;
export type CreditsChangedEvent = z.infer<typeof creditsChangedEventSchema>;
export type BillingAlertEvent = z.infer<typeof billingAlertEventSchema>;
export type StoryboardUpdatedEvent = z.infer<typeof storyboardUpdatedEventSchema>;
export type RealtimeEvent = z.infer<typeof realtimeEventSchema>;
export type RealtimeEventType = RealtimeEvent['type'];

export const realtimeChannelScopes: Record<RealtimeEventType, string> = {
  'job.updated': 'jobs',
  'upload.updated': 'uploads',
  'credits.changed': 'credits',
  'billing.alert': 'billing',
  'storyboard.updated': 'storyboards',
};

export const buildRealtimeChannel = (userId: string, scope: string) => `user:${userId}:${scope}`;

export const resolveRealtimeChannel = (
  userId: string,
  type: RealtimeEventType,
  scopeOverride?: string
) => buildRealtimeChannel(userId, scopeOverride ?? realtimeChannelScopes[type] ?? 'events');
